import Emitter from 'component-emitter'
import throttle from 'lodash/throttle'
import soundManager2 from 'soundmanager2/script/soundmanager2-nodebug-jsmin.js'

const { soundManager } = soundManager2

const SOUND_ID = 'sound'
const clampVolume = value => Math.min(100, Math.max(0, value))

export default function playerService() {
    const events = Emitter({})

    let isLoaded = false

    const statusObj = {
        status: 'stopped',
        currentTime: -1,
        duration: null,
        type: null,
    }

    const settingsObj = {
        volume: 25,
    }

    let prevVolume = 25

    const on = (event, arg) => {
        events.on(event, arg)
    }
    const off = event => {
        events.off(event)
    }

    const emitTimeUpdate = () => {
        events.emit('timeUpdate', statusObj)
    }
    const emitStatusUpdate = () => {
        events.emit('statusUpdate', statusObj)
    }
    const emitSettingsUpdate = () => {
        events.emit('settingsUpdate', settingsObj)
    }

    const play = (file, time = 0, type) => {
        if (!file) return false

        if (isLoaded) {
            soundManager.destroySound(SOUND_ID)
            statusObj.currentTime = 0
        }

        statusObj.status = 'loading'
        isLoaded = true

        soundManager.createSound({
            id: SOUND_ID,
            url: file,
            volume: settingsObj.volume,
        })

        emitStatusUpdate()

        const throttledWhilePlaying = throttle(function () {
            if (!isLoaded || statusObj.status === 'paused') return false

            statusObj.currentTime = this.position
            statusObj.duration = this.duration
            statusObj.status = 'playing'
            statusObj.type = type
            emitTimeUpdate()
        }, 500)

        return new Promise((resolve, reject) => {
            const returnVal = soundManager.play(SOUND_ID, {
                whileplaying: throttledWhilePlaying,
                onload(success) {
                    if (!success) {
                        stop()
                        reject(new Error('check for errors'))
                    } else {
                        resolve(returnVal)
                    }
                    emitStatusUpdate()
                },
            })
        })
    }

    const pause = () => {
        if (statusObj.status === 'paused') {
            statusObj.status = 'playing'
            soundManager.resume(SOUND_ID)
            emitStatusUpdate()
        } else {
            soundManager.pause(SOUND_ID)

            setTimeout(() => {
                statusObj.status = 'paused'
                emitStatusUpdate()
            })
        }
    }

    const stop = () => {
        if (isLoaded) {
            soundManager.pause(SOUND_ID)
            soundManager.destroySound(SOUND_ID)
            isLoaded = false
        }

        setTimeout(() => {
            statusObj.currentTime = -1
            statusObj.status = 'stopped'
            emitStatusUpdate()
        })
    }

    const setVolume = value => {
        settingsObj.volume = value
        soundManager.setVolume(settingsObj.volume)
        emitSettingsUpdate()
    }

    const seekTime = timeMs => {
        if (statusObj.type === 'mix' && soundManager.setPosition(SOUND_ID, timeMs)) {
            statusObj.currentTime = timeMs
            emitTimeUpdate()
        }
    }

    const seekForward = (span = 10000) => {
        seekTime(statusObj.currentTime + span)
    }

    const seekBackwards = (span = 10000) => {
        seekTime(statusObj.currentTime - span)
    }

    const volumeUp = () => {
        setVolume(clampVolume(settingsObj.volume + 10))
    }

    const volumeDown = () => {
        setVolume(clampVolume(settingsObj.volume - 10))
    }

    const toggleMute = () => {
        if (settingsObj.volume !== 0) {
            prevVolume = settingsObj.volume
            setVolume(0)
        } else {
            setVolume(prevVolume)
        }
    }

    return {
        on,
        off,
        play,
        pause,
        stop,
        setVolume,
        seekTime,
        seekForward,
        seekBackwards,
        volumeUp,
        volumeDown,
        toggleMute,
    }
}
